import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import { useFormatText } from '@/hooks/useFormatText';
import {
  TForgotPassword,
  forgotPasswordSchema,
} from '@/schema/forgotPasswordSchema';
import { ForgotPasswordApi } from '@/service/authService';
import { zodResolver } from '@hookform/resolvers/zod';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { formatText } = useFormatText();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TForgotPassword>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: TForgotPassword) => {
    setLoading(true);

    const payload = {
      email: data.email,
      panel_type: 'admin',
    };
    const response = await ForgotPasswordApi(payload);
    if (response?.data?.status) {
      enqueueSnackbar(response?.data?.message, { variant: 'success' });
      navigate('/check-email', {
        state: { email: data.email },
      });
    } else {
      console.error(response?.data?.message);
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col justify-center p-15 md:p-10 w-full lg:max-w-150">
      <div className="w-full flex flex-col items-center">
        <div className="text-center mb-10">
          <p className="pb-2 text-4xl max-md:text-3xl font-bold text-neutral-primary tracking-[-0.36px] leading-11">
            {formatText('forgot-password.title')}
          </p>
          <p className="text-neutral-secondary text-lg leading-7">
            {formatText('forgot-password.sub-title')}
          </p>
        </div>

        <form
          method="POST"
          className="w-full"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-4">
            <Input
              label={formatText('forgot-password.form.email')}
              placeholder={formatText('forgot-password.form.email-placeholder')}
              name="email"
              type="email"
              isRequired
              register={register}
              error={
                errors.email?.message
                  ? formatText(errors.email.message)
                  : undefined
              }
            />
          </div>

          <Button
            label={formatText('forgot-password.form.actions.send-reset-link')}
            type="submit"
            variant="primary"
            loading={loading}
            className="w-full py-3.5! leading-5 mt-6"
          />
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
